import React, { lazy, Suspense } from 'react';

import {
  Drawer,
  ErrorHandler,
  Gutter,
  History,
  LoadingState,
  Tabs,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { STAFFING_REQUEST_DETAILS_DRAWER_QUERY } from '@jobs/graphql';
import { StaffingRequestDetailsContent } from './_staffing-request-details-content';

const VisibilityTab = lazy(() =>
  import('./_staffing-request-visibility-tab').then((mod) => ({
    default: mod.VisibilityTab,
  }))
);

export function StaffingRequestDetailsDrawer({
  open,
  onClose,
  staffingRequestId,
}: {
  open: boolean;
  onClose: () => void;
  staffingRequestId?: string;
}) {
  const currentMembership = useCurrentMembership();

  const { data, loading, error } = useQuery(
    STAFFING_REQUEST_DETAILS_DRAWER_QUERY,
    {
      skip: !staffingRequestId,
      variables: { id: staffingRequestId! },
    }
  );

  const staffingRequest = data?.staffingRequest;

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="StaffingRequestDetailsDrawer"
        messageType="contextual"
      />
    );
  }

  return (
    <Drawer title="Staffing Request Details" onClose={onClose} open={open}>
      <Tabs>
        <Tabs.Pane tab={<Tabs.Tab title="Details" />} key="details">
          <Gutter size="lg" direction="vertical" internal>
            {loading ? (
              <LoadingState />
            ) : (
              <StaffingRequestDetailsContent
                staffingRequest={staffingRequest}
              />
            )}
          </Gutter>
        </Tabs.Pane>
        {currentMembership.canBypassTierVisibility && (
          <Tabs.Pane tab={<Tabs.Tab title="Visibility" />} key="visibility">
            <Gutter size="lg" direction="vertical" internal>
              <Suspense
                fallback={
                  <LoadingState className="wk-compliance-item-drawer__loader" />
                }
              >
                <VisibilityTab staffingRequestId={staffingRequest?.id} />
              </Suspense>
            </Gutter>
          </Tabs.Pane>
        )}
        <Tabs.Pane tab={<Tabs.Tab title="History" />} key="history">
          <Gutter size="lg" direction="vertical" internal>
            {staffingRequest?.history && (
              <History data={staffingRequest?.history} />
            )}
          </Gutter>
        </Tabs.Pane>
      </Tabs>
    </Drawer>
  );
}
